import {
    Grid2,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { ValidatorForm } from "react-material-ui-form-validator";
import { useNavigate, useParams } from "react-router-dom";
import { TicketStatusEnum } from "../../../_common/enum/ticketStatus.enum";
import { TicketTypeEnum } from "../../../_common/enum/ticketType.enum";
import EnumSelect from "../../../_components/autocomplete/enum.select";
import { StandardButton } from "../../../_components/button/primary.button";
import { StandardTextValidator } from "../../../_components/textValidator/standardTextValidator";
import { toastr } from "../../../_components/toastr/toastr";
import { handleErrorResponse } from "../../../_helpers/handleResponse.helper";
import { ticketService } from "../../../_services/ticket.service";
import { TicketAffidavit } from "../_componentsTicketType/ticket-affidavit";
import { TicketDesktopTrace } from "../_componentsTicketType/ticket-desktopTrace";
import { TicketFieldAgentEnquiries } from "../_componentsTicketType/ticket-fieldAgentEnquiries";
import { TicketFullTraceEnquiries } from "../_componentsTicketType/ticket-fullTraceEnquiries";
import { TicketGeneralEnquiries } from "../_componentsTicketType/ticket-generalEnquiries";
import { TicketStatusReport } from "../_componentsTicketType/ticket-statusReport";
import { TicketOccupancyCheck } from "../_componentsTicketType/ticket-occupancyCheck";
import { TicketServe } from "../_componentsTicketType/ticket-serve";
import { TicketSurveillance } from "../_componentsTicketType/ticket-surveillance";
import { TicketSubjectData } from "./ticket-subjectData";
import { DataTypeEnum } from "../../../_common/enum/dataType.enum";
import { TicketResultEnum } from "../../../_common/enum/ticketResult.enum";
import { AgentsWithCaseDistanceMultipleAutoComplete } from "../../../_components/autocomplete/agentsWithCaseDistance.multiple.autocomplete";
import { accountService } from "../../../_services/accounts.service";
import { DataTypeAutoComplete } from "../../../_components/autocomplete/dataType.autocomplete";
import { AgentsMultipleAutoComplete } from "../../../_components/autocomplete/agent.multiple.autocomplete";
import { TicketClientPaymentTerms } from "./ticket-clientPaymentTerms";

export function TicketInfoManage(params: { onSave(): void, setPaymentDate: (date?: string) => void, setInvoiceNumber: (invNumber?: string) => void }) {
    
    const resetData = () => ({});

    const { id, caseId, ticketType } = useParams<{ id: string, caseId: string, ticketType?: string }>();
    const navigate = useNavigate();
    const [data, setData] = useState<any>(resetData);
    const [isLoading, setIsLoading] = useState(false);
    const [saving, setIsSaving] = useState(false);
    const isAdmin = accountService.isAdmin || accountService.isUser;
    const initialTitleRef = useRef(document.title);

    const [isReadOnly, setIsReadOnly] = useState(id != null  ? false : true);

    useEffect(() => {
        if (id != null)
            fetchData();
        else
            setData(resetData);
    }, [id]);

    function fetchData() {
        if (!id) return;

        setIsLoading(true);
        return ticketService
            .get(id, ticketType)
            .then(
                (json) => {
                    if (json && json.ticketAgents && json.ticketAgents.length) {
                        json.ticketAgents = json.ticketAgents.map((e: any) => ({
                            id: e.userId,
                            name: e.user?.name,
                        }));
                    }
                    setData(json);
                    console.log(json);
                    params.setPaymentDate(json?.paymentDate)
                    params.setInvoiceNumber(json?.invoiceNumber)
                }, handleErrorResponse
            )
            .finally(() => {
                setIsLoading(false);
            });
    }

    function submit(e: any) {
        e.preventDefault();
        setIsSaving(true);

        //need this cuz i dont know the ticket type on create and which service i need to send the submit to
        var _ticketType = id == null ? getTicketTypeName(data.ticketTypeId) : ticketType;

        const _data = {
            ...data,
            caseId: caseId,
            ...(id == null && { ticketStatusId: TicketStatusEnum.Open }),
        };

        if (_data.ticketAgents) {
            _data.ticketAgents.forEach((a: { id: any; userId: any; eventId: any; }) => {
                a.userId = a.id;
                a.eventId = _data.id;
                a.id = 0;
            });
        }

        return ticketService
            .submit(_data, _ticketType)
            .then(
                () => {
                    toastr.success("Saved successfully");
                    resetData();
                    params.onSave();
                }, handleErrorResponse
            )
            .finally(() => setIsSaving(false));
    }

    function disable() {
        if (!id) return;
        if (window.confirm("Are you sure you want to delete this ticket?") !== true) return;
        setIsLoading(true);
        return ticketService
            .disable(id, ticketType)
            .then(
                () => {
                    toastr.success("Deleted successfully");
                    //params.onSave();
                    navigate('/Tickets');
                }, handleErrorResponse
            )
            .finally(() => setIsLoading(false));
    }

    function getTicketTypeName(ticketTypeId: any): string {
        const entry = Object.entries(TicketTypeEnum).find(
            ([key, value]) => value === ticketTypeId
        );

        //Filter out the reverse mapping (numeric keys) that TypeScript creates
        if (entry && typeof entry[0] === "string" && isNaN(Number(entry[0]))) {
            return entry[0];
        }

        return "";
    }

    function splitByCapital(text?: string): string {
        if (!text) return "";
        return text
            .replace(/([a-z])([A-Z])/g, "$1 $2")
            .replace(/([A-Z])([A-Z][a-z])/g, "$1 $2")
            .trim();
    }

    function getTicketTypeLabel(): string {
        if (ticketType) {
            return splitByCapital(ticketType);
        }
        return splitByCapital(getTicketTypeName(data.ticketTypeId));
    }

    useEffect(() => {
        return () => {
            document.title = initialTitleRef.current;
        };
    }, []);

    useEffect(() => {
        if (!id) return;
        const refNumber = data?.ticketReferenceNumber;
        const typeLabel = getTicketTypeLabel();
        if (!refNumber || !typeLabel) return;

        document.title = `Aserve - ${refNumber} - ${typeLabel}`;
    }, [id, data?.ticketReferenceNumber, data?.ticketTypeId, ticketType]);

    function handleChange(event: any) {
        setData((_data: any) => ({ ..._data, [event.target.name]: event.target.value }));
    }

    return (
        <ValidatorForm
            onSubmit={submit}
            noValidate>

            <Grid2 container spacing={1} sx={{ "padding": "8px" }}>
                <TicketClientPaymentTerms caseId={caseId ?? data.caseId} />

                {id == null &&
                    <Grid2 size={{ xs: 12 }}>
                        <EnumSelect
                            key="ticketTypeId"
                            enumObject={TicketTypeEnum}
                            label="Select Ticket Type"
                            name="ticketTypeId"
                            value={data.ticketTypeId}
                            onChange={handleChange}
                            required
                            fullWidth
                        />
                    </Grid2>
                }

                {id != null &&
                    <Grid2 size={{ xs: 12 }}>
                        <StandardTextValidator
                            key="ticketType"
                            name="ticketType"
                            label="Ticket Type"
                            fullWidth
                            disabled
                            value={ticketType}
                        />
                    </Grid2>
                }

                <Grid2 size={{ xs: 12 }}>
                    <Grid2 size={{ xs: 12 }}>
                        <DataTypeAutoComplete
                            key="ticketActionListId"
                            dataType={DataTypeEnum.TicketActionLists}
                            label="Select Ticket Action List"
                            hasParent={true}
                            parentId={data.ticketTypeId}
                            value={data.ticketActionListId}
                            name={"ticketActionListId"}
                            onChange={handleChange}
                            required={true}
                            disabled={id == null || data.isImported ? false : true}
                        />
                    </Grid2>
                </Grid2>

                <Grid2 size={{ xs: 12 }}>
                    <DataTypeAutoComplete
                        parentId={caseId ?? data.caseId}
                        key="ticketStatusId"
                        dataType={DataTypeEnum.TicketStatuses}
                        label="Select Ticket Status"
                        hasParent={true}
                        value={id != null ? data.ticketStatusId : 1} //Set to open on Create
                        name={"ticketStatusId"}
                        onChange={handleChange}
                        required={true}
                        disabled={id != null ? false : true}
                    />
                </Grid2>

                {/*Only Show on Create*/}
                {id == null &&
                    <Grid2 size={{ xs: 12 }}>
                        <DataTypeAutoComplete
                            key="caseSubjectId"
                            dataType={DataTypeEnum.CaseSubjects}
                            label="Select Subject"
                            hasParent={true}
                            parentId={caseId}
                            value={data.caseSubjectId}
                            name={"caseSubjectId"}
                            onChange={handleChange}
                            required={true}
                        />
                    </Grid2>
                }

                {data.caseSubjectId > 0 &&
                    <Grid2 size={{ xs: 12 }}>
                            <AgentsWithCaseDistanceMultipleAutoComplete
                                required={false}
                                name={"ticketAgents"}
                                hasParent
                                parentId={data.caseSubjectId}
                                items={data.ticketAgents}
                                onChange={handleChange}
                                disabled={!isAdmin}
                            /> 
                    </Grid2>
                }


                {/*Ticket Subject Data*/}
                {(id != null && data.ticketTypeId != TicketTypeEnum.Affidavit) && <TicketSubjectData subjectData={data.caseSubject} ticketType={data.ticketTypeId} />}

                {/*Ticket Type specific fields*/}
                {data.ticketTypeId === TicketTypeEnum.Serve && <TicketServe data={data} setData={setData} handleChange={handleChange} />}
                {data.ticketTypeId === TicketTypeEnum.FullTraceEnquiries && <TicketFullTraceEnquiries data={data} setData={setData} handleChange={handleChange} />}
                {data.ticketTypeId === TicketTypeEnum.DesktopTrace && <TicketDesktopTrace data={data} setData={setData} handleChange={handleChange} />}
                {data.ticketTypeId === TicketTypeEnum.FieldAgentEnquiries && <TicketFieldAgentEnquiries data={data} setData={setData} handleChange={handleChange} />}
                {data.ticketTypeId === TicketTypeEnum.StatusReport && <TicketStatusReport data={data} setData={setData} handleChange={handleChange} />}
                {data.ticketTypeId === TicketTypeEnum.OccupancyCheck && <TicketOccupancyCheck data={data} setData={setData} handleChange={handleChange} />}
                {data.ticketTypeId === TicketTypeEnum.Surveillance && <TicketSurveillance data={data} setData={setData} handleChange={handleChange} />}
                {data.ticketTypeId === TicketTypeEnum.Affidavit && <TicketAffidavit data={data} setData={setData} handleChange={handleChange} />}
                {data.ticketTypeId === TicketTypeEnum.GeneralEnquiries && <TicketGeneralEnquiries data={data} setData={setData} handleChange={handleChange} />}

                <Grid2 size={{ xs: 12 }}>
                    <EnumSelect
                        key="ticketResultId"
                        enumObject={TicketResultEnum}
                        label="Select Result"
                        name="ticketResultId"
                        value={data.ticketResultId}
                        onChange={handleChange}
                        fullWidth
                    />
                </Grid2>

                {!accountService.isClient &&
                    <Grid2 size={{ xs: 12 }}>
                        <Grid2 container spacing={1}>
                            <Grid2 size={{ xs: data.id == null ? 12 : data?.isActive === false ? 12 : 6 }}>
                                <StandardButton
                                    saving={saving}
                                    loading={isLoading}
                                    icon="save"
                                    type="submit">
                                    {(data?.isActive === false) ? "Save and Reactivate" : "Save"}
                                </StandardButton>
                            </Grid2>

                            {(!!id && data?.isActive === true) &&
                                <Grid2 size={{ xs: 6 }}>
                                    <StandardButton
                                        saving={saving}
                                        loading={isLoading}
                                        icon="block"
                                        variant="error"
                                        onClick={disable}>
                                        Delete
                                    </StandardButton>
                                </Grid2>
                            }
                        </Grid2>
                    </Grid2>
                }
            </Grid2>

        </ValidatorForm>
    );
};